import { z } from 'zod';

// NEXT_PUBLIC_* values are embedded in the browser bundle. This includes RPC
// URLs with provider keys and backend client-identification keys: restrict
// those at the provider, because dashboard visibility cannot make them secret.
// Per-network fields stay optional here so a deployment can ship one or more
// networks; getNetworkConfig enforces each selected network's complete set.

function optionalUrl(): z.ZodTypeAny {
  return z.preprocess(
    (value) => (typeof value === 'string' && value.length === 0 ? undefined : value),
    z.string().url().optional(),
  );
}

export const publicEnvSchema: z.ZodObject<{
  NEXT_PUBLIC_API_URL_LOCAL: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_API_URL_DEVNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_API_URL_MAINNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_NATS_URL_LOCAL: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_NATS_URL_DEVNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_NATS_URL_MAINNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_RPC_URL_LOCAL: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_RPC_URL_DEVNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_RPC_URL_MAINNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_PROGRAM_ID_LOCAL: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_PROGRAM_ID_DEVNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_PROGRAM_ID_MAINNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_API_KEY_LOCAL: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_API_KEY_DEVNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_API_KEY_MAINNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_OAUTH_REDIRECT_URI: z.ZodString;
  NEXT_PUBLIC_STATSIG_CLIENT_KEY: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_APPSFLYER_ONELINK_URL: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_BETTERSTACK_JS_TOKEN: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_DEV_TOOLKIT: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_AGENT_REACT_DEVTOOLS: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_MORFI_BLOCKHASH_RESOLVER_MODE: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_TURNKEY_ORG_ID: z.ZodString;
  NEXT_PUBLIC_TURNKEY_API_BASE_URL: z.ZodString;
  NEXT_PUBLIC_TURNKEY_AUTH_PROXY_CONFIG_ID: z.ZodString;
  NEXT_PUBLIC_TURNKEY_AUTH_PROXY_URL: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_INTERCOM_APP_ID: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_CUSTOMERIO_CDP_WRITE_KEY: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_CUSTOMERIO_SITE_ID: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_CANNY_APP_ID: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_DONATION_RECIPIENT_MAINNET: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_SITE_URL: z.ZodString;
  NEXT_PUBLIC_VERCEL_URL: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_BING_SITE_VERIFICATION: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_APP_VERSION: z.ZodOptional<z.ZodString>;
  NEXT_PUBLIC_DEV_PROXY: z.ZodOptional<z.ZodString>;
}> = z.object({
  NEXT_PUBLIC_API_URL_LOCAL: z.string().optional(),
  NEXT_PUBLIC_API_URL_DEVNET: z.string().optional(),
  NEXT_PUBLIC_API_URL_MAINNET: z.string().optional(),
  NEXT_PUBLIC_NATS_URL_LOCAL: z.string().optional(),
  NEXT_PUBLIC_NATS_URL_DEVNET: z.string().optional(),
  NEXT_PUBLIC_NATS_URL_MAINNET: z.string().optional(),
  NEXT_PUBLIC_RPC_URL_LOCAL: z.string().optional(),
  NEXT_PUBLIC_RPC_URL_DEVNET: z.string().optional(),
  NEXT_PUBLIC_RPC_URL_MAINNET: z.string().optional(),
  NEXT_PUBLIC_PROGRAM_ID_LOCAL: z.string().optional(),
  NEXT_PUBLIC_PROGRAM_ID_DEVNET: z.string().optional(),
  NEXT_PUBLIC_PROGRAM_ID_MAINNET: z.string().optional(),
  NEXT_PUBLIC_API_KEY_LOCAL: z.string().optional(),
  NEXT_PUBLIC_API_KEY_DEVNET: z.string().optional(),
  NEXT_PUBLIC_API_KEY_MAINNET: z.string().optional(),
  NEXT_PUBLIC_OAUTH_REDIRECT_URI: z.string().url(),
  NEXT_PUBLIC_STATSIG_CLIENT_KEY: z.string().optional(),
  NEXT_PUBLIC_APPSFLYER_ONELINK_URL: z.string().url().startsWith('https://').optional(),
  NEXT_PUBLIC_BETTERSTACK_JS_TOKEN: z.string().optional(),
  NEXT_PUBLIC_DEV_TOOLKIT: z.string().optional(),
  NEXT_PUBLIC_AGENT_REACT_DEVTOOLS: z.string().optional(),
  NEXT_PUBLIC_MORFI_BLOCKHASH_RESOLVER_MODE: z.string().optional(),
  NEXT_PUBLIC_TURNKEY_ORG_ID: z.string().min(1),
  NEXT_PUBLIC_TURNKEY_API_BASE_URL: z.string().url(),
  NEXT_PUBLIC_TURNKEY_AUTH_PROXY_CONFIG_ID: z.string().min(1),
  NEXT_PUBLIC_TURNKEY_AUTH_PROXY_URL: z.string().url().optional(),
  NEXT_PUBLIC_INTERCOM_APP_ID: z.string().optional(),
  NEXT_PUBLIC_CUSTOMERIO_CDP_WRITE_KEY: z.string().optional(),
  NEXT_PUBLIC_CUSTOMERIO_SITE_ID: z.string().optional(),
  NEXT_PUBLIC_CANNY_APP_ID: z.string().optional(),
  NEXT_PUBLIC_DONATION_RECIPIENT_MAINNET: z.string().optional(),
  NEXT_PUBLIC_SITE_URL: z.string().url(),
  NEXT_PUBLIC_VERCEL_URL: z.string().optional(),
  NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION: z.string().optional(),
  NEXT_PUBLIC_BING_SITE_VERIFICATION: z.string().optional(),
  NEXT_PUBLIC_APP_VERSION: z.string().optional(),
  NEXT_PUBLIC_DEV_PROXY: z.string().optional(),
});

export const serverEnvSchema: z.ZodObject<{
  VERCEL_ENV: z.ZodOptional<z.ZodString>;
  STATSIG_CONSOLE_API_KEY: z.ZodOptional<z.ZodString>;
  ANTHROPIC_API_KEY: z.ZodOptional<z.ZodString>;
  MAINNET_RPC_URL: z.ZodTypeAny;
  TASKON_CHECK_SECRET: z.ZodOptional<z.ZodString>;
  TASKON_BEARER_TOKEN: z.ZodOptional<z.ZodString>;
}> = z.object({
  VERCEL_ENV: z.string().optional(),
  STATSIG_CONSOLE_API_KEY: z.string().optional(),
  ANTHROPIC_API_KEY: z.string().optional(),
  MAINNET_RPC_URL: optionalUrl(),
  TASKON_CHECK_SECRET: z.string().optional(),
  TASKON_BEARER_TOKEN: z.string().optional(),
});
